/****************************************************
 * Module Name:  kapanHistoryList.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useState } from "react";
import Utility from "../../utility/utility";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import KapanCreationProxy from "../../utility/proxy/manager/manufacturingProxy/kapanCreationProxy";
import CommonSnackbar from "../common/commonSnackBar";
import StoneDetailModal from "../common/stoneDetailModal";
import Constants from "../../utility/constants";
import PacketHistoryModal from "../common/packetHistoryModal";
import { v4 as uuidv4 } from "uuid";
import RemarkModal from "../common/remarkModal";
import EvaluationModal from "../common/evaluationModal";
import NoPermission from "../common/noPermission";
import { useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";


const KapanHistoryList = () => {
    const [data, setData] = useState([]);
    const [packetsIds, setPacketsIds] = useState([]);
    const [packetLoader, setPacketLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [kapanId, setKapanId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [showRemarkModal, setShowRemarkModal] = useState(false);
    const [showEvaluationModal, setShowEvaluationModal] = useState(false);
    const [access, setAccess] = useState(false);
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [showInlineRemark, setShowInlineRemark] = useState(true);
    const [sourcedata, setSourcedataData] = useState([]);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);

    useEffect(() => {
        PageTitle("MANUFACTURING_REPORT_KAPANHISTORYDETAIL");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MANUFACTURING_REPORT_KAPANHISTORYDETAIL
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
    }

    const handleSearch = (packetsIds) => {
        setPacketLoader(true);
        KapanCreationProxy.GetKapanList(
            packetsIds.split(/[ ,]+/).map(Number),
            (response) => {
                if (response) {
                    let data = response
                    for (let i = 0; i < data.length; i++) {
                        data[i].id = uuidv4();
                    }
                    setData(data);
                    setPacketLoader(false);
                } else {
                    setPacketLoader(false);
                    setErrorSnackbar(true);
                }
            }
        );
    };

    const handleClose = () => {
        setShowPacketDetailModal(false);
        setShowRemarkModal(false)
        setShowEvaluationModal(false)
        setShowStoneDetailModal(false)
    };

    const handlePacketDetailModal = (params, kapanid) => {
        setPacketId(params);
        setKapanId(kapanid);
        setShowPacketDetailModal(true);
    };

    const handleRemarkModal = (params) => {
        setSourcedataData(params)
        setShowRemarkModal(true)
    }

    const handleEvaluationModal = (params, kapanid) => {
        setPacketId(params);
        setKapanId(kapanid);
        setShowEvaluationModal(true)
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const columnName = [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            width: 80,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "weight",
            headerName: "WEIGHT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "updatedat",
            headerName: "UPDATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        {
            field: "user",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => params.row.userid + " | " + params.row.username,
        },
        {
            field: "department",
            headerName: "DEPARTMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => params.row.department_id + " | " + params.row.department_name,
        },
        {
            field: "nextdepartmentheadid",
            headerName: "ISSUED DEPARTMENT HEAD",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.nextdepartmentheadid ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.value}</Avatar>}
                            label={params.row.nextdepartmentheadname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                    : '-'
            ),
            disableExport: true,
        },
        {
            field: "nextdepartmentid",
            headerName: "ISSUED DEPARTMENT NAME",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.nextdepartmentid ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.value}</Avatar>}
                            label={params.row.nextdepartmentname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box> : '-'
            ),
            disableExport: true,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            flex: 2,
            minWidth: 350,
            renderCell: (params) => (
                params.row.remarks === null || params.row.remarks === "" ? '' :
                    showInlineRemark ?
                        <Box sx={{ minHeight: "auto" }}>
                            <table style={{ border: '1pt solid black' }}>
                                <tr style={{ border: '1pt solid black' }}>
                                    <th style={{ border: '1pt solid black' }}>Remark Category</th>
                                    <th style={{ border: '1pt solid black' }}>Remarks</th>
                                </tr>
                                {
                                    Utility.GetRemark(params.row.remarks).map((iRow) =>
                                        <tr key={iRow.id} style={{ border: '1pt solid black' }}>
                                            <td style={{ border: '1pt solid black' }}>{iRow.category}</td>
                                            <td style={{ border: '1pt solid black' }}>{iRow.remark}</td>
                                        </tr>
                                    )
                                }
                            </table>
                        </Box>
                        :
                        <Button
                            sx={{ width: 220 }}
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleRemarkModal(params.row.remarks)
                            }}
                        >
                            Show Remark
                        </Button>
            ),
        },
        {
            field: "lossremark",
            headerName: "LOSS REMARK",
            headerAlign: "center",
            align: "center",
            flex: 2,
            minWidth: 350,
            height: 'auto',
            renderCell: (params) => (
                params.row.lossremarks === null || params.row.lossremarks === "" ? '' :
                    showInlineRemark ?
                        <Box sx={{ minHeight: "auto" }}>
                            <table style={{ border: '1pt solid black' }}>
                                <tr style={{ border: '1pt solid black' }}>
                                    <th style={{ border: '1pt solid black' }}>Loss Category</th>
                                    <th style={{ border: '1pt solid black' }}>Loss Remarks</th>
                                </tr>
                                {
                                    Utility.GetRemark(params.row.lossremarks).map((iRow) =>
                                        <tr key={iRow.id} style={{ border: '1pt solid black' }}>
                                            <td style={{ border: '1pt solid black' }}>{iRow.category}</td>
                                            <td style={{ border: '1pt solid black' }}>{iRow.remark}</td>
                                        </tr>
                                    )
                                }
                            </table>
                        </Box>
                        :
                        <Button
                            sx={{ width: 220 }}
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleRemarkModal(params.row.lossremarks)
                            }}
                        >
                            Show Loss Remark
                        </Button>
            ),
        },
        {
            field: "evaluation",
            headerName: "EVALUATION",
            headerAlign: "center",
            align: "center",
            flex: 2,
            minWidth: 350,
            height: 'auto',
            renderCell: (params) => (
                <Button
                    sx={{ width: 220 }}
                    variant="contained"
                    onClick={(e) => {
                        e.stopPropagation()
                        handleEvaluationModal(params.row.packetid, params.row.kapanid)
                    }}
                >
                    Show Evaluation
                </Button>
            ),
        },
    ];

    const rows = data;
    const columns = columnName;

    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        {showPacketDetailModal ? (
                            <PacketHistoryModal
                                show={showPacketDetailModal}
                                packetId={packetId}
                                kapanId={kapanId}
                                close={handleClose}
                            />
                        ) : null}
                        {showEvaluationModal ? (
                            <EvaluationModal
                                show={showEvaluationModal}
                                packetId={packetId}
                                kapanId={kapanId}
                                handleClose={handleClose}
                            />
                        ) : null}
                        {showRemarkModal ? (
                            <RemarkModal
                                show={showRemarkModal}
                                sourceData={sourcedata}
                                handleClose={handleClose}
                            />
                        ) : null}
                        {showStoneDetailModal ? (
                            <StoneDetailModal
                                show={showStoneDetailModal}
                                stoneId={stoneId}
                                handleClose={handleClose}
                            />
                        ) : null}
                        <Box display={"flex"} flexDirection="row" width={"70vw"} mt={"40px"} mb={1}>
                            <Box display={"flex"} width="100%">
                                <Typography variant="h6" width="125px" mt={2}>
                                    Kapan Ids
                                </Typography>
                                <TextField
                                    className="picker"
                                    type={"number"}
                                    id="outlined-multiline-static"
                                    label="Enter Kapan ID"
                                    placeholder="Comma seperated Kapan Ids"
                                    multiline
                                    fullWidth
                                    rows={2}
                                    value={packetsIds}
                                    onChange={(e) =>
                                        setPacketsIds(Utility.ConvertNewLineToComma(e.target.value))
                                    }
                                />
                            </Box>
                            <LoadingButton
                                variant="contained"
                                size="large"
                                onClick={() => handleSearch(packetsIds)}
                                loading={packetLoader}
                                loadingPosition="start"
                                startIcon={<SearchOutlinedIcon size="large" />}
                                sx={{ mt: 1, alignSelf: "center", ml: 2 }}
                                disabled={packetsIds.length === 0}
                            >
                                Search
                            </LoadingButton>
                            <FormGroup sx={{ mt: 1, alignSelf: "center", ml: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            disabled={packetLoader}
                                            checked={showInlineRemark}
                                            onChange={() => setShowInlineRemark(!showInlineRemark)}
                                        />
                                    }
                                    label="Inline Loss Remark"
                                />
                            </FormGroup>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "4px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "5px 5px",
                                    borderRadius: "4px",
                                },
                                height: "80vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "8px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "15px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "22px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                slots={{ toolbar: GridToolbar }}
                                getRowHeight={() => "auto"}
                                rows={rows}
                                columns={columns}
                                getRowId={(row) => row.id}
                                loading={packetLoader}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            // id: false,
                                        },
                                    },
                                    sorting: {
                                        sortModel: [{ field: "updatedat", sort: "desc" }],
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                {packetLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(packetsIds)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                        {/* <Box height={100}>
                    <TotalStockComponent
                        data={data}
                        sizeColumn={"size"}
                        priceColumn={"price"}
                    />
                </Box> */}
                    </Box>
                )
            }
        </div>
    );
};

export default KapanHistoryList;
